import logger from '../utils/logger.js';
import { escapeHtml } from '../utils/html-utils.js';
import { renderSafetyArmadillo } from '../utils/mascots.js';
import { appendReturnTo } from '../utils/return-to-url.js';

class DocsPage extends HTMLElement {
  constructor() {
    super();
    this.sections = [];
    this.article = null;
    this.loading = true;
    this.error = null;
  }

  connectedCallback() {
    this.render();
    this.loadContent();
  }

  parsePath() {
    const path = window.location.pathname.replace(/\/$/, '') || '/docs';
    if (path === '/docs') {
      return { view: 'hub' };
    }
    const parts = path.split('/').filter(Boolean);
    if (parts[0] !== 'docs') {
      return { view: 'hub' };
    }
    if (parts.length === 2) {
      return { view: 'section', section: parts[1] };
    }
    if (parts.length >= 3) {
      return { view: 'article', section: parts[1], slug: parts.slice(2).join('/') };
    }
    return { view: 'hub' };
  }

  async loadContent() {
    const route = this.parsePath();
    try {
      if (route.view === 'article') {
        const res = await fetch(`/api/docs/${encodeURIComponent(route.section)}/${encodeURIComponent(route.slug)}`, {
          credentials: 'include'
        });
        if (!res.ok) throw new Error('Article not found');
        this.article = await res.json();
      } else {
        const res = await fetch('/api/docs', { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load docs');
        const data = await res.json();
        this.sections = data.sections || [];
      }
      this.route = route;
      this.loading = false;
      this.render();
      this.attachListeners();
    } catch (err) {
      logger.error('Docs load error:', err);
      this.error = err.message;
      this.loading = false;
      this.render();
    }
  }

  attachListeners() {
    this.querySelector('[data-action="open-armadillo"]')?.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('open-support'));
    });
  }

  docsHref(path) {
    return appendReturnTo(path);
  }

  renderReturnNav() {
    return '<return-nav></return-nav>';
  }

  renderSupportCard() {
    return `
      <section class="docs-support-card card" aria-label="Safety Armadillo support">
        <div class="docs-support-mascot">${renderSafetyArmadillo()}</div>
        <div class="docs-support-body">
          <h2>Need help from a human?</h2>
          <p>The Safety Armadillo handles bugs, support questions, and safety reports — separate from these how-to guides.</p>
          <button type="button" class="btn btn-primary" data-action="open-armadillo">Open Safety Armadillo</button>
        </div>
      </section>
    `;
  }

  renderHub() {
    const cards = this.sections.map((sec) => `
      <a class="docs-section-card card" href="${escapeHtml(this.docsHref(`/docs/${sec.id}`))}">
        <h2>${escapeHtml(sec.title)}</h2>
        <p>${sec.articles.length} article${sec.articles.length === 1 ? '' : 's'}</p>
      </a>
    `).join('');

    return `
      <article class="public-page docs-page" aria-label="Documentation">
        ${this.renderReturnNav()}
        <header class="docs-page-header">
          <h1>Docs</h1>
          <p class="docs-page-intro">Guides for making the most of Fun Old Times.</p>
        </header>
        ${this.renderSupportCard()}
        <div class="docs-section-grid">${cards}</div>
      </article>
    `;
  }

  renderSection(sectionId) {
    const sec = this.sections.find((s) => s.id === sectionId);
    if (!sec) {
      return '<article class="public-page docs-page"><p class="docs-error">Section not found.</p></article>';
    }
    const links = sec.articles.map((a) => `
      <a class="docs-article-link card" href="${escapeHtml(this.docsHref(`/docs/${sec.id}/${a.slug}`))}">
        <h3>${escapeHtml(a.title)}</h3>
        ${a.description ? `<p>${escapeHtml(a.description)}</p>` : ''}
      </a>
    `).join('');

    return `
      <article class="public-page docs-page" aria-label="${escapeHtml(sec.title)}">
        ${this.renderReturnNav()}
        <nav class="docs-breadcrumb"><a href="${escapeHtml(this.docsHref('/docs'))}">Docs</a> → ${escapeHtml(sec.title)}</nav>
        <header class="docs-page-header"><h1>${escapeHtml(sec.title)}</h1></header>
        <div class="docs-article-list">${links}</div>
      </article>
    `;
  }

  renderArticle() {
    const a = this.article;
    return `
      <article class="public-page docs-page docs-article-body" aria-label="${escapeHtml(a.title)}">
        ${this.renderReturnNav()}
        <nav class="docs-breadcrumb">
          <a href="${escapeHtml(this.docsHref('/docs'))}">Docs</a> →
          <a href="${escapeHtml(this.docsHref(`/docs/${a.section}`))}">${escapeHtml(a.sectionTitle)}</a> →
          ${escapeHtml(a.title)}
        </nav>
        <header class="docs-page-header"><h1>${escapeHtml(a.title)}</h1></header>
        <div class="docs-article-content">${a.html}</div>
      </article>
    `;
  }

  render() {
    if (this.loading) {
      this.innerHTML = `
        <article class="public-page docs-page">
          <div class="loading-state"><span class="spinner"></span><p>Loading docs…</p></div>
        </article>
      `;
      return;
    }
    if (this.error) {
      this.innerHTML = `
        <article class="public-page docs-page">
          <p class="docs-error">${escapeHtml(this.error)}</p>
          <a href="${escapeHtml(this.docsHref('/docs'))}" class="btn btn-primary">Back to Docs</a>
        </article>
      `;
      return;
    }

    const route = this.route || this.parsePath();
    if (route.view === 'article') {
      this.innerHTML = this.renderArticle();
    } else if (route.view === 'section') {
      this.innerHTML = this.renderSection(route.section);
    } else {
      this.innerHTML = this.renderHub();
    }
  }
}

customElements.define('docs-page', DocsPage);
